/**
 * AP-13: Edit/New-Seite für TimeUserRef.
 *
 * Routen:
 *   /time-tracking/users/new
 *   /time-tracking/users/:id/edit
 *
 * AP-36 Phase E: Im Bearbeiten-Modus speichert jedes Feld beim Verlassen — ohne
 * Knopf. Der Anlegen-Modus bleibt ein Formular mit lokalem Zustand und dem Knopf
 * „Anlegen".
 *
 * Die Verknüpfung zu einem internen User (userId) geschieht NICHT hier, sondern
 * über den `LinkUserDialog` in der Listenansicht. Hier nur die Stamm-Info.
 */
import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { api } from '@/lib/axios';
import {
  FeldStatus,
  feldKlasse,
  mitStand,
  useEntwurf,
  useFeld,
  useSpeicherstelle,
  useStellenDaten,
  type Datensatz,
  type Speicherstelle,
} from '@/lib/speichern';
import {
  createUserRef,
  deleteUserRef,
  getUserRef,
  getRefSchemas,
} from '../api/time-master-data.api';
import { CustomFieldsForm, validateRequired } from '../components/CustomFieldsForm';
import { ModeBanner } from '../components/ModeBanner';
import { useDataSourceMode } from '../hooks/useDataSourceMode';
import {
  fieldLabel,
  fieldWrapper,
  formCard,
  inputBase,
  pageTitle,
} from '@/lib/form-styles';
import type { CustomFieldsSchema, TimeUserRef } from '../types/time-master-data.types';
import { bestaetige } from '@/lib/dialoge';

interface FormState {
  firstName: string;
  lastName: string;
  email: string;
  isActive: boolean;
  customFields: Record<string, unknown>;
}

const EMPTY: FormState = { firstName: '', lastName: '', email: '', isActive: true, customFields: {} };

const ladeText = <div className="p-6 text-sm text-muted-foreground">Wird geladen …</div>;

/** Pflichtfelder der Zusatzfelder — dieselbe Prüfung wie früher vor dem Speichern. */
function pflichtfelderText(schema: CustomFieldsSchema | null, werte: Record<string, unknown>): string | null {
  const fehlend = Object.keys(validateRequired(schema, werte));
  return fehlend.length === 0 ? null : `Pflichtfeld fehlt: ${fehlend.join(', ')}`;
}

export default function TimeUserRefFormPage() {
  const { id } = useParams<{ id?: string }>();
  return id ? <BearbeitenSeite id={id} /> : <AnlegenSeite />;
}

// ── Bearbeiten: feldweise, ohne Knopf ───────────────────────────────────────

function BearbeitenSeite({ id }: { id: string }) {
  const navigate = useNavigate();
  const { canEdit, isLoading: modeLoading } = useDataSourceMode('userRef');

  const [row, setRow] = useState<TimeUserRef | null>(null);
  const [schema, setSchema] = useState<CustomFieldsSchema | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        const [schemas, geladen] = await Promise.all([getRefSchemas(), getUserRef(id)]);
        if (cancelled) return;
        setSchema(schemas.userRef);
        setRow(geladen);
      } catch (e) {
        if (!cancelled) setError((e as Error).message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => { cancelled = true; };
  }, [id]);

  const stelle = useSpeicherstelle<Datensatz>({
    schluessel: `timeUserRef:${id}`,
    bezeichnung: [row?.firstName, row?.lastName].filter(Boolean).join(' ') || row?.email || 'Mitarbeiter',
    daten: row ? (row as unknown as Datensatz) : null,
    schreibe: (patch, stand) =>
      api.patch<Datensatz>(`/time-master-data/users/${id}`, patch, mitStand(stand)).then((r) => r.data),
    keepalive: { url: `/time-master-data/users/${id}`, method: 'PATCH' },
  }) as Speicherstelle<unknown>;

  const gesperrt = !canEdit;
  const vorname  = useFeld<string | null>(stelle, 'firstName', { label: 'Vorname',  gesperrt });
  const nachname = useFeld<string | null>(stelle, 'lastName',  { label: 'Nachname', gesperrt });
  const email    = useFeld<string | null>(stelle, 'email',     { label: 'E-Mail',   gesperrt });
  const aktiv    = useFeld<boolean>(stelle, 'isActive', { label: 'aktiv', art: 'schalter', gesperrt });

  // Die Zusatzfelder sind ein Eintrag: bearbeitet wird ein Entwurf, gespeichert
  // wird er beim Verlassen der Hülle (der Abschnitt darunter).
  const daten = useStellenDaten(stelle);
  const gespeicherteZusatzfelder = useMemo(
    () => (daten.customFields as Record<string, unknown> | null) ?? {},
    [daten.customFields],
  );
  const zusatzfelder = useEntwurf<Record<string, unknown>>(stelle, {
    kennung: 'customFields',
    gespeichert: gespeicherteZusatzfelder,
    felder: ['customFields'],
    label: 'Zusatzfelder',
    gesperrt,
    operation: (entwurf) => () => ({ customFields: entwurf }),
    pruefe: (entwurf) => pflichtfelderText(schema, entwurf),
  });

  const deaktivieren = async () => {
    if (!(await bestaetige('Mitarbeiter wirklich deaktivieren? Der Eintrag bleibt erhalten und kann später wieder aktiviert werden.'))) return;
    setError(null);
    try { await deleteUserRef(id); navigate('/time-tracking/users'); }
    catch (e) { setError((e as Error).message); }
  };

  if (loading || modeLoading) return ladeText;

  return (
    <div className="p-6 space-y-4 max-w-3xl">
      <header className="flex items-center justify-between">
        <h1 className={pageTitle}>Mitarbeiter bearbeiten</h1>
        <button type="button" onClick={() => navigate('/time-tracking/users')} className="text-sm text-muted-foreground hover:underline">
          ← Zurück zur Liste
        </button>
      </header>

      <ModeBanner kind="userRef" />

      {error && <div className="rounded-md border border-red-400 bg-red-50 px-3 py-2 text-sm text-red-700">{error}</div>}

      <section className={formCard + ' space-y-4'}>
        <div className="grid grid-cols-2 gap-4">
          <div className={fieldWrapper}>
            <label className={fieldLabel}>Vorname</label>
            <input type="text" className={feldKlasse} {...vorname.props} />
            <FeldStatus status={vorname} />
          </div>
          <div className={fieldWrapper}>
            <label className={fieldLabel}>Nachname</label>
            <input type="text" className={feldKlasse} {...nachname.props} />
            <FeldStatus status={nachname} />
          </div>
        </div>

        <div className={fieldWrapper}>
          <label className={fieldLabel}>E-Mail</label>
          <input type="email" className={feldKlasse} {...email.props} />
          <FeldStatus status={email} />
        </div>

        <div>
          <label className="flex items-center gap-2 select-none">
            <input type="checkbox" className="h-4 w-4" {...aktiv.schalter} />
            <span className="text-sm">aktiv</span>
          </label>
          <FeldStatus status={aktiv} />
        </div>

        <p className="text-xs text-muted-foreground">
          Die Verknüpfung zu einem internen Symphenia-User erfolgt in der Listenansicht
          („Verknüpfen"-Aktion). Niemals automatisch anhand der E-Mail.
        </p>
      </section>

      {schema && schema.fields.length > 0 && (
        <section className={formCard + ' space-y-3'} {...zusatzfelder.huelle}>
          <h2 className="text-base font-semibold text-primary">Zusatzfelder</h2>
          <CustomFieldsForm schema={schema} value={zusatzfelder.entwurf ?? {}}
            onChange={(v) => zusatzfelder.setze(v)}
            disabled={gesperrt} />
          <FeldStatus status={zusatzfelder} />
        </section>
      )}

      <div>
        <button type="button" onClick={deaktivieren} disabled={gesperrt}
          className="px-3 py-2 text-sm rounded-md border border-red-400 text-red-700 hover:bg-red-50 disabled:opacity-50">
          Deaktivieren
        </button>
      </div>
    </div>
  );
}

// ── Anlegen: Formular mit Knopf ─────────────────────────────────────────────

function AnlegenSeite() {
  const navigate = useNavigate();
  const { canEdit, isLoading: modeLoading } = useDataSourceMode('userRef');

  const [form, setForm] = useState<FormState>(EMPTY);
  const [schema, setSchema] = useState<CustomFieldsSchema | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [cfErrors, setCfErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    let cancelled = false;
    getRefSchemas()
      .then((schemas) => { if (!cancelled) setSchema(schemas.userRef); })
      .catch((e) => { if (!cancelled) setError((e as Error).message); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  const disabled = !canEdit || saving;

  const submit = async () => {
    setError(null);
    if (!form.lastName.trim() && !form.firstName.trim() && !form.email.trim()) {
      setError('Mindestens Name oder E-Mail angeben.');
      return;
    }
    const reqErrors = validateRequired(schema, form.customFields);
    if (Object.keys(reqErrors).length > 0) {
      setCfErrors(reqErrors);
      setError('Bitte Pflichtfelder ausfüllen.');
      return;
    }
    setCfErrors({});
    setSaving(true);
    try {
      await createUserRef({
        firstName: form.firstName.trim() || null,
        lastName:  form.lastName.trim()  || null,
        email:     form.email.trim()     || null,
        isActive:  form.isActive,
        customFields: form.customFields,
      });
      navigate('/time-tracking/users');
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setSaving(false);
    }
  };

  if (loading || modeLoading) return ladeText;

  return (
    <div className="p-6 space-y-4 max-w-3xl">
      <header className="flex items-center justify-between">
        <h1 className={pageTitle}>Neuer Mitarbeiter</h1>
        <button type="button" onClick={() => navigate('/time-tracking/users')} className="text-sm text-muted-foreground hover:underline">
          ← Zurück zur Liste
        </button>
      </header>

      <ModeBanner kind="userRef" />

      {error && <div className="rounded-md border border-red-400 bg-red-50 px-3 py-2 text-sm text-red-700">{error}</div>}

      <section className={formCard + ' space-y-4'}>
        <div className="grid grid-cols-2 gap-4">
          <div className={fieldWrapper}>
            <label className={fieldLabel}>Vorname</label>
            <input type="text" className={inputBase}
              value={form.firstName}
              onChange={(e) => setForm((f) => ({ ...f, firstName: e.target.value }))}
              disabled={disabled} />
          </div>
          <div className={fieldWrapper}>
            <label className={fieldLabel}>Nachname</label>
            <input type="text" className={inputBase}
              value={form.lastName}
              onChange={(e) => setForm((f) => ({ ...f, lastName: e.target.value }))}
              disabled={disabled} />
          </div>
        </div>

        <div className={fieldWrapper}>
          <label className={fieldLabel}>E-Mail</label>
          <input type="email" className={inputBase}
            value={form.email}
            onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
            disabled={disabled} />
        </div>

        <label className="flex items-center gap-2 select-none">
          <input type="checkbox" checked={form.isActive}
            onChange={(e) => setForm((f) => ({ ...f, isActive: e.target.checked }))}
            disabled={disabled} className="h-4 w-4" />
          <span className="text-sm">aktiv</span>
        </label>

        <p className="text-xs text-muted-foreground">
          Die Verknüpfung zu einem internen Symphenia-User erfolgt in der Listenansicht
          („Verknüpfen"-Aktion). Niemals automatisch anhand der E-Mail.
        </p>
      </section>

      {schema && schema.fields.length > 0 && (
        <section className={formCard + ' space-y-3'}>
          <h2 className="text-base font-semibold text-primary">Zusatzfelder</h2>
          <CustomFieldsForm schema={schema} value={form.customFields}
            onChange={(v) => setForm((f) => ({ ...f, customFields: v }))}
            disabled={disabled} />
          {Object.keys(cfErrors).length > 0 && (
            <ul className="text-xs text-red-600 list-disc list-inside">
              {Object.entries(cfErrors).map(([k, v]) => <li key={k}>{k}: {v}</li>)}
            </ul>
          )}
        </section>
      )}

      <div className="flex justify-end gap-2">
        <button type="button" onClick={() => navigate('/time-tracking/users')}
          className="px-3 py-2 text-sm rounded-md border" disabled={saving}>
          Abbrechen
        </button>
        <button type="button" onClick={submit} disabled={disabled}
          className="px-3 py-2 text-sm rounded-md bg-primary text-primary-foreground hover:opacity-90 disabled:opacity-50">
          {saving ? 'Legt an …' : 'Anlegen'}
        </button>
      </div>
    </div>
  );
}
